import type { FC, ReactNode } from "react";
import { useMemo } from "react";

import { EmptyState } from "@similarweb/ui-components";

import type { SubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import Chart from "pro-features/components/Chart/src/Chart";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";

import LOBLegends from "../../../components/LOBLegends";
import { useVisitsOvertimeCompareMode } from "../compare/useVisitsOvertimeCompareMode";
import { ChartContainer, Container } from "../styledComponent";

import CategorySelectDropdown from "./CategorySelectDropdown/CategorySelectDropdown";
import { allTrackers } from "app/services/track/track";

interface IVisitsOvertimeCompareModeProps {
    subtitleFilters: SubtitleFilters;
}

const VisitsOvertimeCompareMode: FC<IVisitsOvertimeCompareModeProps> = ({ subtitleFilters }) => {
    const columnTitles: [string, string] = [
        translate("analysis.content.lob.legend.title.value.domain"),
        translate("analysis.content.lob.legend.title.value.monthly.visits"),
    ];

    const {
        selectedLob,
        handleSelectedCategory,
        config,
        legendItems,
        visibleChartData,
        chartData,
        setLegendItems,
        isLoading,
        availableLobs,
        lobVisitsData,
        excelUrl,
    } = useVisitsOvertimeCompareMode();

    const renderHeader = useMemo((): ReactNode => {
        return (
            <CategorySelectDropdown
                availableLobs={availableLobs}
                onCategoryClicked={handleSelectedCategory}
                selectedLob={selectedLob}
                lobVisitsData={lobVisitsData}
            />
        );
    }, [selectedLob, availableLobs, lobVisitsData]);

    const hasVisibleItems = legendItems.some((item) => item.visible);

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `Lines of business over time graph - download excel`);
    };

    return (
        <WidgetContainer
            data-automation="visits-overtime-widget"
            title={renderHeader}
            tooltip={translate("analysis.content.visits.overtime.tooltip")}
            subtitleFilters={subtitleFilters}
            isLoading={isLoading}
            noData={!chartData.length}
            withBorder
            showSubtitleFiltersLoadingState={false}
            loaderHeight={290}
            loaderWidth="100%"
            isExcelDisabled={false}
            excelUrl={excelUrl}
            hideExcelButton={false}
            onExcelClick={handleExcelClick}
        >
            <Container>
                <ChartContainer>
                    {visibleChartData.length && hasVisibleItems ? (
                        <Chart
                            type="area"
                            data={visibleChartData}
                            config={config}
                        />
                    ) : (
                        <EmptyState
                            title=""
                            subtitle={translate("analysis.content.lob.no.data")}
                        />
                    )}
                </ChartContainer>
                {legendItems.length > 0 ? (
                    <LOBLegends
                        legendItems={legendItems}
                        setLegendItems={setLegendItems}
                        eventName="over time"
                        columnTitles={columnTitles}
                        hasSearch={false}
                        height="212px"
                    />
                ) : null}
            </Container>
        </WidgetContainer>
    );
};

export default VisitsOvertimeCompareMode;
